import { clamp01 } from '@/features/landing/stroke/stroke-clock'

// The scroll is the timeline (landing spec decision 3). Document-space px.
export const DWELL_VH = 0.2 // §7.1: beat 02 waits a fifth of a viewport
export const GAP_VH = 1 // §7.2: a gap is about one viewport
// §7.2 (v1.1): gap 1 carries two beats and ends at 0.7 exposed, so at one
// viewport plus the dwell its whole scrub — five strokes and the verdict —
// was 0.3 of a viewport, under one flick: the drawing was skipped and the
// verdict arrived with page 2 already over it. At one and a half the scrub
// is 0.6 of a viewport, 0.4 for the strokes and 0.2 for the verdict.
export const GAP1_VH = 1.5
export const VERDICT_FROM = 2 / 3 // §7.6: the verdict holds the last third
// §7.2: a gap's beat runs while at least this much of the ground is
// exposed. It begins when the previous page's bottom edge has passed
// this far up the viewport (the ground is half exposed beneath it) and
// completes when the next page's top edge has risen this far (the
// ground is still half exposed above it), then holds. With a
// one-viewport gap the scrub spans exactly the gap. Without the lead,
// every lifting page exposed the previous beat's held still; without the
// early end, every beat finished under the incoming page.
export const EXPOSED_FRACTION = 0.5
// §7.2: gaps 1 and 2 end earlier, while the ground is still this much
// exposed, because their beats' focus is taller than half a viewport:
// the canvas with its verdict line, the test's glyph with its answer
// field. Gap 1 is also the one gap whose scrub starts with the ground
// fully exposed (page 1 is gone before the dwell ends), so page 2 rises
// over the draw beat for the whole scrub. The camera keeps the focus
// above the line, so the last strokes, the verdict and the typed word
// are seen.
export const TALL_FOCUS_EXPOSED_FRACTION = 0.7

export type Beat = 1 | 2 | 3 | 4 | 5 | 6

export interface WalkMetrics {
    vh: number
    page1Bottom: number
    gaps: Array<{ top: number; height: number }>
}

export interface WalkState {
    beat: Beat
    t: number
}

// §7.1: gap 1's trigger is a rule, not geometry — beat 02 starts once
// page 1's bottom edge is above the viewport's top edge plus the dwell,
// so beat 01 is the rest state whatever the hero's height. Later gaps
// begin on the exposure rule above. Every scrub ends on the exposure
// rule. Beats 02 and 03 share gap 1.
export function walkState(scrollY: number, m: WalkMetrics): WalkState {
    let state: WalkState = { beat: 1, t: 0 }
    const lead = (1 - EXPOSED_FRACTION) * m.vh
    m.gaps.forEach((g, k) => {
        const trigger = k === 0 ? m.page1Bottom + DWELL_VH * m.vh : g.top - lead
        const exposed = k <= 1 ? TALL_FOCUS_EXPOSED_FRACTION : EXPOSED_FRACTION
        const end = g.top + g.height - exposed * m.vh
        if (scrollY < trigger) return
        const t = clamp01((scrollY - trigger) / Math.max(1, end - trigger))
        if (k === 0) {
            state =
                t < VERDICT_FROM
                    ? { beat: 2, t: t / VERDICT_FROM }
                    : { beat: 3, t: (t - VERDICT_FROM) / (1 - VERDICT_FROM) }
        } else {
            state = { beat: (k + 3) as Beat, t }
        }
    })
    return state
}

// How much of the ground a beat's gap still exposes when its scrub
// completes: the line the next page's top has reached, as a fraction of
// the viewport. The ground's camera keeps the beat's focus above it.
// Beats 01 and 06 have no page rising over them.
export function exposedAtEnd(beat: Beat): number | null {
    if (beat === 2 || beat === 3 || beat === 4) {
        return TALL_FOCUS_EXPOSED_FRACTION
    }
    if (beat === 5) return EXPOSED_FRACTION
    return null
}

// Progress of a sticky stage over its track: 0 while the track's top is
// below the viewport top, 1 once the stage has reached the track's end.
export function trackProgress(
    top: number,
    height: number,
    vh: number,
    scrollY: number
): number {
    const span = Math.max(1, height - vh)
    return clamp01((scrollY - top) / span)
}
